import { useAppRouter } from '@/common/route/useAppRouter';
import { StringUtils } from '@/common/utils/stringUtils';
import { useCrudActions } from '@/common/view/useCrudActions';
import { useGameVariantList } from '@/modules/game/variant/model/list';
import { gameService } from '@/services/game';
import { onMounted, reactive, watch } from 'vue';
import { useRoute } from 'vue-router';

export function useMixins() {
    const route = useRoute();
    const router = useAppRouter();
    const crudAction = useCrudActions();

    const { variantOptions, variantNameMap, fetchList: fetchVariantList } = useGameVariantList();

    const model = reactive({
        id: StringUtils.toNumber(route.params.id),
        variantId: StringUtils.toNumber(route.query.variantId),
        name: '',
        type: GameMatchType.Normal.value,
        maxRound: 0,
        maxPoint: 0,
        tableCreditRatio: '',
        costs: '',
        costStates: '',
        potRatePercents: '',
        potRateWeights: '',
        levelTimeout: 0,
        findingMatchTimeout: null,
        winLoseGroup: false,
        allowInvitation: false,
        canDelete: false,
        canEdit: false,

        get variantOptions() {
            return variantOptions.value;
        },
        get isShowFindingMatchTimeout() {
            return this.type == GameMatchType.Normal.value;
        }
    });

    const fetch = crudAction.executeAction({
        canExecute: () => model.id != null,
        request: () => gameService.match.get(model.id),
        onSuccess: ({ data }) => Object.assign(model, data),
        onError: () => router.go(-1)
    });

    const changeType = (type: number) => {
        if (type != GameMatchType.Normal.value) {
            model.findingMatchTimeout = null;
        }
    };

    // monitor change of bet type to change lock type
    watch(() => model.type, changeType);

    onMounted(async () => {
        await fetchVariantList();
        await fetch();
    });

    return { model, fetch, variantNameMap, variantOptions };
}

export const GameMatchType = {
    Normal: { value: 1, label: 'Normal', background: 'success' },
    Premium: { value: 2, label: 'Premium', background: 'primary' },
    Duplicate: { value: 3, label: 'Duplicate', background: 'info' },
    Scenario: { value: 4, label: 'Scenario', background: 'info' }
};

export const GameMatchCostState = {
    STATES: {
        Visible: { value: 1, label: 'Visible', background: 'primary' },
        Hidden: { value: 2, label: 'Hidden', background: 'secondary' }
    },
    SEPARATOR: StringUtils.Characters.Semicolon,
    previewStates(input: string): { label: string; background: string }[] {
        return input
            .split(this.SEPARATOR)
            .map(s => s.trim())
            .filter(s => s !== '')
            .map(s => {
                const found = Object.values(this.STATES).find(o => o.value === Number(s));
                return found
                    ? { label: found.label, background: found.background }
                    : { label: `?(${s})`, background: 'danger' };
            });
    },
    previewCostStates(costs: string, states: string): { cost: string; label: string }[] {
        const c = (costs || '')
            .split(this.SEPARATOR)
            .map(item => item.trim())
            .filter(item => item !== '');
        const s = (states || '')
            .split(this.SEPARATOR)
            .map(item => item.trim())
            .filter(item => item !== '');
        const n = Math.max(c.length, s.length);
        return Array.from({ length: n }, (_, i) => {
            const found = Object.values(this.STATES).find(o => o.value === Number(s[i]));
            return {
                cost: c[i] ?? '?',
                label: found ? found.label : `?(${s[i] ?? ''})`
            };
        });
    }
};

export const GameMatchOptions = {
    GameMatchType: Object.entries(GameMatchType).map(([, data]) => data)
};
